import React, { useState, useLayoutEffect, useMemo, useEffect } from "react";
import {
  getAppDisplayTimezone,
  getAppWallDateParts,
  getAppTodayIso,
  instantFromAppWallParts,
  parseDbDateTime,
  resolveAppInstant,
} from "../utils/appDateTime";

/** Earliest instant on the 5-minute grid that is >= d */
function ceilToFiveMinuteBoundary(d) {
  const step = 5 * 60 * 1000;
  const t = d.getTime();
  return new Date(Math.ceil(t / step) * step);
}

function partsFromInstant(instant, timeZone, defaultTime = null) {
  const resolved = resolveAppInstant(instant);
  const fallback = !resolved && defaultTime;
  const source = resolved || new Date();
  const wall = getAppWallDateParts(source, timeZone);
  if (!wall) {
    const now = new Date();
    return {
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      day: now.getDate(),
      hour: fallback ? defaultTime.hour : now.getHours(),
      minute: fallback ? defaultTime.minute : now.getMinutes(),
    };
  }
  return {
    year: wall.year,
    month: wall.month,
    day: wall.day,
    hour: fallback ? defaultTime.hour : wall.hour,
    minute: fallback ? defaultTime.minute : wall.minute,
  };
}

function buildInstantFromParts(parts, timeZone) {
  return instantFromAppWallParts(
    {
      year: parts.year,
      month: parts.month,
      day: parts.day,
      hour: parts.hour,
      minute: parts.minute,
      second: 0,
    },
    timeZone,
  );
}

export default function DateTimePickerPanel({
  value,
  onChange,
  mode = "single",
  onContinue,
  onDone,
  onBack,
  /** If set, user cannot select a date/time before this instant. Snapped up to 5-minute steps. */
  minDateTime = null,
  /** Optional fallback clock time for an empty value, e.g. { hour: 0, minute: 0 }. */
  defaultTime = null,
  timeZone = getAppDisplayTimezone(),
}) {
  const defaultHour =
    defaultTime && Number.isFinite(defaultTime.hour)
      ? Math.min(23, Math.max(0, defaultTime.hour))
      : null;
  const defaultMinute =
    defaultTime && Number.isFinite(defaultTime.minute)
      ? Math.min(59, Math.max(0, defaultTime.minute))
      : null;
  const minTs =
    minDateTime == null ? null : resolveAppInstant(minDateTime)?.getTime();
  const min = useMemo(() => {
    if (minTs == null || Number.isNaN(minTs)) return null;
    return ceilToFiveMinuteBoundary(new Date(minTs));
  }, [minTs]);

  const initParts = useMemo(() => {
    const normalizedDefaultTime =
      defaultHour == null || defaultMinute == null
        ? null
        : { hour: defaultHour, minute: defaultMinute };
    const base = partsFromInstant(value, timeZone, normalizedDefaultTime);
    if (!min) return base;
    const built = buildInstantFromParts(base, timeZone);
    if (built && built >= min) return base;
    return partsFromInstant(min, timeZone);
  }, [value, min, timeZone, defaultHour, defaultMinute]);

  const [viewYear, setViewYear] = useState(initParts.year);
  const [viewMonth, setViewMonth] = useState(initParts.month - 1);
  const [selDay, setSelDay] = useState(initParts.day);
  const [selHour, setSelHour] = useState(initParts.hour);
  const [selMin, setSelMin] = useState(initParts.minute);
  const [showAllMinutes, setShowAllMinutes] = useState(
    () => initParts.minute % 5 !== 0,
  );

  useEffect(() => {
    const next = initParts;
    setViewYear(next.year);
    setViewMonth(next.month - 1);
    setSelDay(next.day);
    setSelHour(next.hour);
    setSelMin(next.minute);
    setShowAllMinutes(next.minute % 5 !== 0);
  }, [initParts.year, initParts.month, initParts.day, initParts.hour, initParts.minute]);

  const buildDate = () =>
    buildInstantFromParts(
      {
        year: viewYear,
        month: viewMonth + 1,
        day: selDay,
        hour: selHour,
        minute: selMin,
      },
      timeZone,
    );

  useLayoutEffect(() => {
    if (!min) return;
    const built = buildDate();
    if (!built || built >= min) return;
    const t = partsFromInstant(min, timeZone);
    setViewYear(t.year);
    setViewMonth(t.month - 1);
    setSelDay(t.day);
    setSelHour(t.hour);
    setSelMin(t.minute);
  }, [min, viewYear, viewMonth, selDay, selHour, selMin, timeZone]);

  const MONTH_NAMES = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const DAY_LABELS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

  const MINUTES = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
  const ALL_MINUTES = useMemo(
    () => Array.from({ length: 60 }, (_, i) => i),
    [],
  );
  const minuteOptions = showAllMinutes ? ALL_MINUTES : MINUTES;

  const HOURS = Array.from({ length: 24 }, (_, h) => {
    const ampm = h >= 12 ? "pm" : "am";
    const h12 = h % 12 || 12;
    return { label: `${h12}${ampm}`, value: h };
  });

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const rawFirst = new Date(viewYear, viewMonth, 1).getDay();
  const firstOffset = rawFirst === 0 ? 6 : rawFirst - 1;

  const cells = [
    ...Array(firstOffset).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewYear((y) => y - 1);
      setViewMonth(11);
    } else setViewMonth((m) => m - 1);
  };

  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewYear((y) => y + 1);
      setViewMonth(0);
    } else setViewMonth((m) => m + 1);
  };

  const todayIso = getAppTodayIso(timeZone);
  const todayParts = parseDbDateTime(`${todayIso}T12:00:00Z`);
  const todayWall = todayParts ? getAppWallDateParts(todayParts, timeZone) : null;

  const isToday = (d) =>
    todayWall &&
    d === todayWall.day &&
    viewMonth + 1 === todayWall.month &&
    viewYear === todayWall.year;

  const isDayDisabled = (d) => {
    if (!d || !min) return false;
    const lastSlot = buildInstantFromParts(
      { year: viewYear, month: viewMonth + 1, day: d, hour: 23, minute: 55 },
      timeZone,
    );
    return lastSlot && lastSlot < min;
  };

  const lastDayPrevMonth = buildInstantFromParts(
    {
      year: viewMonth === 0 ? viewYear - 1 : viewYear,
      month: viewMonth === 0 ? 12 : viewMonth,
      day: new Date(viewYear, viewMonth, 0).getDate(),
      hour: 23,
      minute: 55,
    },
    timeZone,
  );
  const prevNavDisabled = Boolean(min && lastDayPrevMonth && lastDayPrevMonth < min);

  const isHourDisabled = (h) => {
    if (!min) return false;
    const lastInHour = buildInstantFromParts(
      { year: viewYear, month: viewMonth + 1, day: selDay, hour: h, minute: 55 },
      timeZone,
    );
    return lastInHour && lastInHour < min;
  };

  const isMinuteDisabled = (m) => {
    if (!min) return false;
    const t = buildInstantFromParts(
      { year: viewYear, month: viewMonth + 1, day: selDay, hour: selHour, minute: m },
      timeZone,
    );
    return t && t < min;
  };

  const fmtTime = () => {
    const ampm = selHour >= 12 ? "PM" : "AM";
    const h12 = selHour % 12 || 12;
    return `${h12}:${String(selMin).padStart(2, "0")} ${ampm}`;
  };

  const emitChange = () => {
    let date = buildDate();
    if (min && date && date < min) date = new Date(min);
    onChange(date);
    return date;
  };

  const handleDone = () => {
    const date = emitChange();
    if (onDone) onDone(date);
  };

  const handleContinue = () => {
    const date = emitChange();
    if (onContinue) onContinue(date);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-[20020] overflow-y-auto">
      <div className="min-h-screen flex items-center justify-center p-4">
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-[700px] p-6"
          style={{ maxHeight: "90vh", overflowY: "auto" }}
        >
          <div className="flex gap-8 flex-wrap md:flex-nowrap">
            {/* Calendar */}
            <div style={{ minWidth: 260 }}>
              <div className="flex items-center justify-between mb-3">
                <span className="font-bold text-gray-900 text-base">
                  {MONTH_NAMES[viewMonth]} {viewYear}
                </span>

                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={prevMonth}
                    disabled={prevNavDisabled}
                    className={`p-1 rounded text-lg ${
                      prevNavDisabled
                        ? "text-gray-300 cursor-not-allowed"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    onClick={nextMonth}
                    className="p-1 rounded hover:bg-gray-100 text-lg"
                  >
                    ›
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-7 mb-1">
                {DAY_LABELS.map((d) => (
                  <div
                    key={d}
                    className="text-center text-[10px] text-gray-400 font-semibold py-1"
                  >
                    {d}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-y-1">
                {cells.map((d, i) => {
                  const disabled = !d || isDayDisabled(d);
                  return (
                    <button
                      type="button"
                      key={i}
                      disabled={disabled}
                      onClick={() => d && !isDayDisabled(d) && setSelDay(d)}
                      className={`w-8 h-8 mx-auto flex items-center justify-center text-sm rounded-full
                      ${!d ? "invisible" : ""}
                      ${
                        disabled && d
                          ? "text-gray-300 cursor-not-allowed line-through"
                          : ""
                      }
                      ${
                        d && !disabled && d === selDay
                          ? "bg-red-600 text-white font-bold"
                          : d && !disabled && isToday(d)
                            ? "text-red-600 font-bold"
                            : d && !disabled
                              ? "text-gray-700 hover:bg-gray-100"
                              : ""
                      }
                    `}
                    >
                      {d || ""}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Time picker */}
            <div className="flex-1">
              <div className="text-right text-xl font-semibold text-gray-900 mb-3">
                {fmtTime()}
              </div>

              <div className="flex gap-4">
                <div className="flex-1">
                  <div className="text-[10px] text-gray-400 text-center mb-2">
                    HOUR
                  </div>

                  <div className="grid grid-cols-4 gap-1 max-h-[200px] overflow-y-auto pr-1">
                    {HOURS.map((h) => {
                      const dis = isHourDisabled(h.value);
                      return (
                        <button
                          type="button"
                          key={h.value}
                          disabled={dis}
                          onClick={() => !dis && setSelHour(h.value)}
                          className={`h-8 text-xs rounded-full
                          ${
                            dis
                              ? "text-gray-300 cursor-not-allowed"
                              : h.value === selHour
                                ? "bg-red-600 text-white"
                                : "hover:bg-gray-100"
                          }
                        `}
                        >
                          {h.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div
                  className={showAllMinutes ? "flex-[1.2]" : ""}
                  style={{ minWidth: showAllMinutes ? 140 : 80 }}
                >
                  <div className="text-[10px] text-gray-400 text-center mb-2">
                    MINUTE
                  </div>

                  <div
                    className={`grid gap-1 ${
                      showAllMinutes
                        ? "grid-cols-4 max-h-[200px] overflow-y-auto pr-1"
                        : "grid-cols-2"
                    }`}
                  >
                    {minuteOptions.map((m) => {
                      const dis = isMinuteDisabled(m);
                      return (
                        <button
                          type="button"
                          key={m}
                          disabled={dis}
                          onClick={() => !dis && setSelMin(m)}
                          className={`h-8 text-xs rounded-full
                          ${
                            dis
                              ? "text-gray-300 cursor-not-allowed"
                              : m === selMin
                                ? "bg-red-600 text-white"
                                : "hover:bg-gray-100"
                          }
                        `}
                        >
                          {m}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between mt-6 pt-4 border-t">
            {mode === "from" ? (
              <button
                type="button"
                onClick={handleContinue}
                className="text-red-600 font-bold text-sm"
              >
                CONTINUE
              </button>
            ) : (
              <button
                type="button"
                onClick={handleDone}
                className="text-red-600 font-bold text-sm"
              >
                DONE
              </button>
            )}

            {mode === "from" ? (
              <button
                type="button"
                onClick={handleDone}
                className="font-bold text-sm"
              >
                DONE
              </button>
            ) : (
              <button type="button" onClick={onBack} className="font-bold text-sm">
                BACK
              </button>
            )}

            <button
              type="button"
              onClick={() => setShowAllMinutes((v) => !v)}
              className="font-bold text-sm"
            >
              {showAllMinutes ? "LESS" : "MORE"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
